import React, { forwardRef, useCallback, useState } from "react";

import { useTranslation } from "utils";

import { TILLAGE_BLANK_PRACTICE } from "consts";

import { PracticeFCPO } from "models";

import { Flex, Form, Icon, Link } from "components";

import { TillageApplicationForm } from "pages/app/all-field-view/components/forms/tillage/TillageApplicationForm";
import { TillageYesNoForm } from "pages/app/all-field-view/components/forms/tillage/TillageYesNoForm";
import { determinePracticeSettings } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { PracticeConfigForBulk } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { InputRequirementConfig } from "pages/app/all-field-view/utils/getInputRequirementConfig";

export type AddEditTillageFormProps = {
  cropId: string;
  disabled: boolean;
  existingTillages: PracticeFCPO[];
  baselineYears: number[] | undefined;
  year: number;
  recommendedSeedingDate?: string;
  requirementConfig: InputRequirementConfig | null;
  optionalInputsAreShown: boolean;
  practiceConfigForBulk?: PracticeConfigForBulk;
};

export const AddEditTillageForm = forwardRef<HTMLDivElement, AddEditTillageFormProps>(
  (
    {
      cropId,
      existingTillages,
      baselineYears,
      disabled,
      year,
      recommendedSeedingDate,
      requirementConfig,
      optionalInputsAreShown,
      practiceConfigForBulk,
    }: AddEditTillageFormProps,
    ref
  ) => {
    const { t } = useTranslation(["allFieldView"]);
    const form = Form.useFormInstance();

    const { defaultToggleState, existingPractices, toggleDisabled, hasMixedValues } = determinePracticeSettings({
      practiceConfigForBulk: practiceConfigForBulk,
      practices: existingTillages,
      disabled,
    });

    const [tillageApplications, setTillageApplications] = useState(() => existingPractices);
    const [isShowingTillageOptions, setIsShowingTillageOptions] = useState<boolean | null>(defaultToggleState);

    const addNewTillageApplication = useCallback(() => {
      setTillageApplications((prev) => [...prev, TILLAGE_BLANK_PRACTICE(cropId)]);
    }, [cropId]);

    const deleteTillageApplication = (index: number) => {
      setTillageApplications((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);

        // update form
        const values = form.getFieldsValue(true);
        delete values[`tillage-${index}`];
        form.setFieldsValue(values);

        // update UI
        if (updated.length === 0) {
          form.setFieldValue("isTillageApplied", null);
          setIsShowingTillageOptions(null);
        }
        return updated;
      });
    };

    const handleTillageYesNoToggle = useCallback(
      (value: boolean) => {
        setIsShowingTillageOptions(() => {
          // if the first application is blank and the user chooses "Yes", add a new tillage application.
          if (tillageApplications.length === 0 && value === true) {
            setTillageApplications([TILLAGE_BLANK_PRACTICE(cropId)]);
          }
          return value;
        });
      },
      [cropId, tillageApplications.length]
    );

    const AddTillageButton = () => (
      <div className="bottom-cta">
        <Link className="bottom-cta button-text" dataTestId="add-edit-tillage-form-add-tillage" onClick={addNewTillageApplication} disabled={disabled}>
          <Flex align="center">
            <Icon iconName="klim/plus" className="plus-icon inline" />
            <span>{t("tillageForm.addTillage")}</span>
          </Flex>
        </Link>
      </div>
    );
    return (
      <div data-testid="add-edit-tillage-form" ref={ref}>
        <TillageYesNoForm
          value={isShowingTillageOptions}
          onChange={(e) => handleTillageYesNoToggle(e.target.value)}
          tillageApplications={tillageApplications}
          baselineYears={baselineYears}
          disabled={toggleDisabled}
          year={year}
          requirementLevel={requirementConfig?.tillages}
          hasMixedValues={hasMixedValues}
          showMessage={practiceConfigForBulk?.showMessage}
        />
        {isShowingTillageOptions
          ? tillageApplications.map((application, index) => (
              <div key={`tillage-${application.id}-${index}`}>
                {index + 1 === tillageApplications.length ? (
                  <>
                    <TillageApplicationForm
                      tillage={application}
                      index={index}
                      onDelete={() => deleteTillageApplication(index)}
                      recommendedSeedingDate={recommendedSeedingDate}
                      requirementConfig={requirementConfig}
                      optionalInputsAreShown={optionalInputsAreShown}
                    />
                    <AddTillageButton />
                  </>
                ) : (
                  <TillageApplicationForm
                    tillage={application}
                    index={index}
                    onDelete={() => deleteTillageApplication(index)}
                    recommendedSeedingDate={recommendedSeedingDate}
                    requirementConfig={requirementConfig}
                    optionalInputsAreShown={optionalInputsAreShown}
                  />
                )}
              </div>
            ))
          : null}
      </div>
    );
  }
);
